import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { YesNoSchema } from "lib/zod";
import type { SurveyInfo } from "../api";
import { useSurveyStore } from "../store";
import {
  Card,
  ButtonArea,
  Button,
  Form,
  StepHeader,
  RadioGroup,
} from "../components";

type Props = {
  surveyInfo: SurveyInfo;
  progress?: string;
  onNextClick: () => void;
  onBackClick: () => void;
};

const schema = z.object({
  employmentConcurrency: z.enum(["Single", "Concurrent"]),
  sameEmployer: YesNoSchema,
  employerPromotion: YesNoSchema,
  continuedEmploymentCondition: YesNoSchema,
});

type Inputs = z.infer<typeof schema>;

export function EmploymentConditionsStep(props: Props) {
  const { surveyInfo, progress, onNextClick, onBackClick } = props;
  const [state, setState] = useSurveyStore((store) => [
    store.employmentConditions,
    store.setEmploymentConditions,
  ]);

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<Inputs>({
    defaultValues: state,
    resolver: zodResolver(schema),
  });

  const handleBackClick = () => {
    setState(getValues());
    onBackClick();
  };

  const handleNextClick = async () => {
    await handleSubmit((data) => {
      setState(data);
      onNextClick();
    })();
  };

  return (
    <Card progress={progress}>
      <StepHeader surveyInfo={surveyInfo} />

      <Form>
        <RadioGroup<Inputs["employmentConcurrency"]>
          vertical
          label="Which of the following best describes your current employment?"
          options={[
            { label: "One job/position", value: "Single" },
            {
              label: "More than one job/position at the same time",
              value: "Concurrent",
            },
          ]}
          error={errors?.employmentConcurrency?.message}
          {...register("employmentConcurrency")}
        />
        <RadioGroup<Inputs["sameEmployer"]>
          vertical
          label="Is your current position with the same employer that you were employed with before they enrolled in Front-End Development?"
          options={[
            { label: "Yes", value: "YES" },
            { label: "No", value: "NO" },
          ]}
          error={errors?.sameEmployer?.message}
          {...register("sameEmployer")}
        />
        <RadioGroup<Inputs["employerPromotion"]>
          vertical
          label="Was your employment after graduation a promotion (with increased pay) due (at least in part) to graduating from/completing the program?"
          options={[
            { label: "Yes", value: "YES" },
            { label: "No", value: "NO" },
          ]}
          error={errors?.employerPromotion?.message}
          {...register("employerPromotion")}
        />
        <RadioGroup<Inputs["continuedEmploymentCondition"]>
          vertical
          label="Was graduating from/completing the program a condition for continued employment with this employer?"
          options={[
            { label: "Yes", value: "YES" },
            { label: "No", value: "NO" },
          ]}
          error={errors?.continuedEmploymentCondition?.message}
          {...register("continuedEmploymentCondition")}
        />
      </Form>

      <ButtonArea>
        <Button label="Back" onClick={handleBackClick} />
        <Button primary label="Continue" onClick={handleNextClick} />
      </ButtonArea>
    </Card>
  );
}
